import { useEffect, useState, MouseEvent, Dispatch, SetStateAction } from "react";
import { useGetTagsTagsGet } from "../../../api/generated/fastAPI";
import { CircularProgress } from "@mui/material";

import { ReactComponent as CheckIcon } from '../../../assets/Icons/adornments/Check.svg';
import { ReactComponent as CancelIcon } from '../../../assets/Icons/adornments/Close.svg';
import SelectField from "../../Form/SelectFields";

export default function TagsEditableContainer({
    arrValues,
    setArrValues,
    isLoading,
    confirmUpdate,
    cancelUpdate,
}: {
    arrValues: string[],
    setArrValues: Dispatch<SetStateAction<string[]>>,
    isLoading: boolean,
    confirmUpdate: (e: any) => void,
    cancelUpdate: (e:any) => void,
}) {
    const getTagsMutation = useGetTagsTagsGet();
    
    const [ allTags, setAllTags ] = useState<{ [key: string]: string[] } | null>(null);
    const [ categoryOptions, setCategoryOptions ] = useState<{ value: string | number, label: string | number }[]>([]);
    const [ tagOptions, setTagOptions ] = useState<{ value: string | number, label: string | number }[]>([]);

    const [ selectedCategory, setSelectedCategory ] = useState('general');
    const [ selectedTag, setSelectedTag ] = useState('');

    useEffect(() => {
        if (getTagsMutation.data) {
            const tagsData = getTagsMutation.data as unknown as { [key: string]: string[] };
            setAllTags(tagsData);

            let categories: { value: string | number, label: string | number }[] = [];

            Object.keys(tagsData).forEach(key => {
                if (key === 'general') {
                    categories.push({ value: key, label: 'Uncategorized' });
                } else {
                    categories.push({ value: key, label: key })
                }
            });

            setCategoryOptions(categories);
        }
    }, [getTagsMutation.data])

    useEffect(() => {
        if (allTags && selectedCategory) {
            const matchingCategory = Object.keys(allTags).find(key => key === selectedCategory);
            if (matchingCategory) {
                const matchingTags = allTags[matchingCategory];
                setTagOptions(matchingTags.map(tag => ({ value: tag, label: tag })))
            } else {
                setTagOptions([]);
            }
        }
    }, [allTags, selectedCategory]);



    const handleSelect = (e: {value: string | number, label: string | number} | {value: string | number, label: string | number}[], name: string) => {
        const value = Array.isArray(e) ? e.map(val => val.value) : e.value;
        
        if (name === 'category') {
            setSelectedCategory(value as string);
            setSelectedTag('');
        } else {
            setSelectedTag(value as string);
        }
    }

    const addTag = (e: MouseEvent<HTMLButtonElement>, category: string, tag: string) => {
        e.preventDefault();

        let addedTag = '';

        if (category === 'general') {
            addedTag = tag;
        } else {
            addedTag = `${category}::${tag}`
        }

        setArrValues(arrValues.concat(addedTag));
        setSelectedTag('');
    }

    const removeTag = (e: MouseEvent<HTMLButtonElement>, index: number) => {
        e.preventDefault();
        setArrValues(arrValues.filter((a, i) => i !== index));
    }


    return (
        <div className="flex flex-col justify-start items-start gap-6 w-full">
            <p className="text-[10px] text-disable">Click the Add button to add a tag</p>

            <div className="flex flex-col justify-start items-start gap-3 w-full">
                <div className="w-full flex justify-start items-end gap-4">
                    <div className="w-1/2">
                        <SelectField 
                            label="Category"
                            id='category'
                            name='category'
                            options={categoryOptions}
                            value={categoryOptions.length > 0 ? categoryOptions.find(opt => opt.value === selectedCategory)! : null}
                            isRequired={false}
                            handleChange={handleSelect}
                            isCreatable
                            isMulti={false}
                        />
                    </div>
                    <div className="w-full grow">
                        <SelectField 
                                label="Tag"
                                id='tag'
                                name='tag'
                                options={tagOptions}
                                value={selectedTag ? { value: selectedTag, label: selectedTag } : null}
                                isRequired={false}
                                handleChange={handleSelect}
                                isCreatable
                                isMulti={false}
                            />
                    </div>
                    <button 
                        disabled={!selectedCategory || !selectedTag}
                        onClick={(e:any) => addTag(e, selectedCategory, selectedTag)} 
                        className={`flex justify-between items-center gap-4 px-1 py-3 bg-none font-medium text-highlight hover:text-primary transition-all `}
                    >
                        Add
                    </button>
                </div>

                <div className="flex justify-center items-start gap-2 w-full">
                <div className="w-full p-2 border border-outline rounded-lg">
                    {arrValues.length > 0 && (
                        <div className="w-full flex flex-wrap justify-start items-start gap-2 max-h-[250px] overflow-y-auto">
                            {arrValues.map((tag, i) => (
                                <div className="py-2 px-4 rounded-lg bg-secondary flex justify-start items-center gap-1">
                                    <p className="font-semibold text-primary text-[12px]">{tag}</p>
                                    <button onClick={(e:any) => removeTag(e, i)} className="w-[18px]"><CancelIcon/></button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className='flex justify-center items-center gap-1'>
                    {isLoading ? (
                        <CircularProgress color='inherit' size={14}/>
                    ) : (
                        <button onClick={confirmUpdate} className='text-disable hover:text-primary transition-all w-[22px]'>
                            <CheckIcon/>
                        </button>
                    )}
                    <button onClick={cancelUpdate} className='text-disable hover:text-primary transition-all w-[26px]'>
                        <CancelIcon/>
                    </button>
                </div>
                </div>
            </div>
        </div>
    )
}